import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, CartesianGrid, ResponsiveContainer, Tooltip } from "recharts";
import { ChevronRight, BarChart3, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const MES_LONGO = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const short = (m) => `${MES[Number(m.slice(5, 7)) - 1]}/${m.slice(2, 4)}`;
const long = (m) => `${MES_LONGO[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const signed = (v) => `${v < 0 ? "− " : ""}${BRL(Math.abs(v))}`;

const MonthCard = ({ m, onOpen }) => (
  <button type="button" onClick={() => onOpen(m.month)} data-testid={`cashflow-month-${m.month}`}
    className="w-full text-left bg-white rounded-2xl border border-slate-200 card-shadow p-4 flex items-start gap-3 hover:border-emerald-300 transition-colors">
    <BarChart3 className="w-6 h-6 text-sky-700 shrink-0 mt-0.5" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-slate-700">{MES_LONGO[Number(m.month.slice(5, 7)) - 1]}</p>
      <p className={`text-xl font-black tabular-nums ${m.balance < 0 ? "text-rose-700" : "text-emerald-700"}`}>{signed(m.balance)}</p>
      <p className="text-xs text-slate-500 tabular-nums">Entradas: {BRL(m.entradas)}</p>
      <p className="text-xs text-slate-500 tabular-nums">Saídas: {BRL(m.saidas)}</p>
    </div>
    <ChevronRight className="w-5 h-5 text-sky-700 shrink-0" />
  </button>
);

const MonthDialog = ({ month, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => { if (month) { setD(null); api.get(`/finance/cashflow/${month}`).then((r) => setD(r.data)).catch(() => setD({ items: [] })); } }, [month]);
  const groups = useMemo(() => {
    const g = {};
    for (const i of d?.items || []) { const k = `${i.type}|${i.group}`; (g[k] = g[k] || { ...i, total: 0, list: [] }).total += i.amount; g[k].list.push(i); }
    return Object.values(g).sort((a, b) => (a.type === b.type ? b.total - a.total : a.type === "entrada" ? -1 : 1));
  }, [d]);
  return (
    <Dialog open={!!month} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col w-[calc(100vw-1.5rem)] sm:w-full [&>*]:min-w-0" data-testid="cashflow-month-dialog">
        <DialogHeader className="text-left shrink-0"><DialogTitle className="font-heading font-black text-slate-900">{month && long(month)}</DialogTitle></DialogHeader>
        {!d ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
          <>
            <div className="grid grid-cols-3 gap-2 shrink-0">
              <div className="rounded-xl bg-emerald-50 p-2.5"><p className="text-[10px] font-bold text-emerald-700 uppercase">Entradas</p><p className="text-sm font-black tabular-nums text-emerald-800">{BRL(d.entradas)}</p></div>
              <div className="rounded-xl bg-rose-50 p-2.5"><p className="text-[10px] font-bold text-rose-700 uppercase">Saídas</p><p className="text-sm font-black tabular-nums text-rose-800">{BRL(d.saidas)}</p></div>
              <div className="rounded-xl bg-slate-100 p-2.5"><p className="text-[10px] font-bold text-slate-600 uppercase">Balanço</p><p className={`text-sm font-black tabular-nums ${d.balance < 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="cashflow-dialog-balance">{signed(d.balance)}</p></div>
            </div>
            <div className="overflow-y-auto min-h-0 flex-1 space-y-3">
              {groups.length === 0 && <p className="text-sm text-slate-500 italic text-center py-6">Nenhum movimento neste mês.</p>}
              {groups.map((g) => (
                <details key={`${g.type}${g.group}`} className="rounded-xl border border-slate-200 group" data-testid={`cashflow-group-${g.group}`}>
                  <summary className="flex items-center gap-2 px-3 py-2.5 cursor-pointer list-none">
                    {g.type === "entrada" ? <TrendingUp className="w-4 h-4 text-emerald-600" /> : <TrendingDown className="w-4 h-4 text-rose-600" />}
                    <span className="flex-1 text-sm font-bold text-slate-800">{g.group} <span className="text-[11px] font-semibold text-slate-400">({g.list.length})</span></span>
                    <span className={`text-sm font-black tabular-nums ${g.type === "entrada" ? "text-emerald-700" : "text-rose-700"}`}>{g.type === "entrada" ? "+ " : "− "}{BRL(g.total)}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-90" />
                  </summary>
                  <ul className="divide-y divide-slate-100 border-t border-slate-100 px-3">
                    {g.list.map((i, k) => (
                      <li key={k} className="py-2 flex items-center gap-2 text-xs">
                        <span className="text-slate-400 w-16 shrink-0">{formatDateBR(i.date)}</span>
                        <span className="flex-1 min-w-0 truncate text-slate-700">{i.title}</span>
                        <span className="font-bold tabular-nums text-slate-800">{BRL(i.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const CashflowChart = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/finance/cashflow").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const chart = data.months.map((m) => ({ name: short(m.month), month: m.month, Entradas: m.entradas, Saídas: m.saidas }));
  return (
    <div className="space-y-3" data-testid="cashflow-chart">
      <section className="bg-white rounded-2xl border border-slate-200 card-shadow p-4">
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} barGap={6} onClick={(e) => e?.activePayload?.[0] && setOpen(e.activePayload[0].payload.month)}>
              <CartesianGrid strokeDasharray="6 6" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="name" tickLine={false} axisLine={{ stroke: "#cbd5e1" }} tick={{ fontSize: 12, fill: "#64748b", fontWeight: 700 }} />
              <Tooltip formatter={(v) => BRL(v)} cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="Entradas" fill="#14b8a6" radius={[4, 4, 0, 0]} maxBarSize={44} />
              <Bar dataKey="Saídas" fill="#be3b62" radius={[4, 4, 0, 0]} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-4 mt-2 text-xs font-semibold text-slate-600">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#14b8a6]" /> Entradas</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#be3b62]" /> Saídas</span>
        </div>
      </section>
      <div>
        <h2 className="font-heading text-base font-black text-slate-900">Balanço por mês</h2>
        <p className="text-[11px] text-slate-500">Atualizado em {formatDateBR(data.updated_at.slice(0, 10))} • toque no mês para ver entradas e saídas</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {[...data.months].reverse().map((m) => <MonthCard key={m.month} m={m} onOpen={setOpen} />)}
      </div>
      <MonthDialog month={open} onClose={() => setOpen(null)} />
    </div>
  );
};
